import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatDate, formatDateTime, formatLocation, formatMnt, formatRate, formatRub, formatSerialDisplay,
  OPENING_PURCHASE_NOTE,
} from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  CLAIM_RESOLUTION_LABEL, COST_ROLES, MATERIAL_LABEL, MOVEMENT_LABEL,
  PAYMENT_LABEL, RETURN_DISPOSITION_LABEL,
  type ItemDetail, type Location,
} from '../lib/types';
import {
  Button, Card, CardTitle, Empty, ErrorNote, Field, Input, Modal, Money, OkNote,
  Select, Spinner, StatusBadge, Textarea,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { PhotoPanel } from '../components/photos';


type Action = 'price' | 'move' | 'writeoff' | 'label' | null;

/**
 * `/items/:id/timeline` — гутлын БҮХ түүх нэг урсгалд: хөдөлгөөн, үнэ,
 * шошго, борлуулалт, хүчингүй болголт, буцаалт, баталгааны нэхэмжлэл,
 * актлалт.
 */
interface TimelineEntry {
  type:
    | 'movement' | 'price' | 'label' | 'sale' | 'void'
    | 'return' | 'claim' | 'write_off' | string;
  at: string;
  data: Record<string, unknown>;
}

/** Нэрлэсэн хүн — `movedBy`, `changedBy` г.м. */
interface Actor { username?: string; fullName?: string }

/** Тэмдэглэлийн төрөл бүрийг нэг мөрөнд буулгана. */
function describe(e: TimelineEntry): {
  kind: string; tone: string; action: string; detail: string; who: string;
} {
  // Timeline нь нэгтгэсэн урсгал тул төрөл бүр өөр талбартай —
  // унших мөчид л нарийвчилна
  const d = e.data as {
    reason?: string | { labelMn?: string } | null; note?: string | null; notes?: string | null;
    fromLocation?: { name: string } | null; toLocation?: { name: string } | null;
    movedBy?: Actor; changedBy?: Actor; soldBy?: Actor; approvedBy?: Actor;
    processedBy?: Actor; resolvedBy?: Actor; voidedBy?: Actor;
    oldPrice?: string; newPrice?: string;
    code?: string; lineTotalMnt?: string; storeName?: string; sellerName?: string;
    serial?: string; labeledBy?: Actor; status?: string;
    buyerName?: string | null; buyerPhone?: string | null;
    warrantyEndsAt?: string; payment?: string;
    disposition?: string; refundMnt?: string; repairCostMnt?: string;
    defectType?: { labelMn?: string }; rejectReason?: { labelMn?: string } | null;
    defectDescription?: string | null; resolution?: string;
    lossMnt?: string;
  };
  /** Шалтгаан нь мөр (үнэ, хүчингүй), эсвэл лавлахын мөр (буцаалт, актлалт). */
  const reasonText =
    typeof d.reason === 'string' ? d.reason : (d.reason?.labelMn ?? null);
  if (e.type === 'movement') {
    return {
      kind: 'Хөдөлгөөн',
      tone: 'bg-accent-100 text-accent-700',
      action: MOVEMENT_LABEL[String(d.reason)] ?? String(d.reason),
      detail: `${d.fromLocation?.name ?? '—'} → ${d.toLocation?.name ?? '—'}${d.note ? ` · ${d.note}` : ''}`,
      who: d.movedBy?.username ?? '—',
    };
  }
  if (e.type === 'price') {
    return {
      kind: 'Үнэ',
      tone: 'bg-warn-bg text-warn-ink',
      action: 'Үнэ өөрчлөгдсөн',
      detail: `${d.oldPrice ? formatMnt(d.oldPrice) : '—'} → ${formatMnt(d.newPrice)}${reasonText ? ` · ${reasonText}` : ''}`,
      who: d.changedBy?.username ?? '—',
    };
  }
  if (e.type === 'sale' || e.type === 'order') {
    /*
      ХЭНД ЗАРСАН нь энэ мөрийн гол утга. Худалдан авагчийн нэр, утас нь
      `order_lines.snapshot`-оос — зарагдсан мөчид хөлдсөн утга
      (CLAUDE.md дүрэм 5), одоогийн `customers` хүснэгтээс БИШ.
      Эрхгүй хүнд (агуулах, цуглуулагч) сервер тэр талбаруудыг огт
      илгээхгүй тул энд ердөө хоосон үлдэнэ.
    */
    const buyer = [d.buyerName, d.buyerPhone].filter(Boolean).join(' · ');
    return {
      kind: 'Борлуулалт',
      tone: 'bg-info-bg text-info-ink',
      action: `${d.code ? String(d.code) : 'Захиалга'}${d.status === 'VOIDED' ? ' · хүчингүй' : ''}`,
      detail: [
        formatMnt(d.lineTotalMnt),
        buyer || null,
        d.storeName ?? null,
        d.payment ? (PAYMENT_LABEL[d.payment] ?? d.payment) : null,
        d.warrantyEndsAt ? `баталгаа ${formatDate(d.warrantyEndsAt)} хүртэл` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      who: d.soldBy?.fullName ?? d.sellerName ?? d.soldBy?.username ?? '—',
    };
  }
  if (e.type === 'void') {
    return {
      kind: 'Хүчингүй',
      tone: 'bg-alert-bg text-alert-ink',
      action: `${d.code ?? 'Захиалга'} хүчингүй болсон`,
      detail: reasonText ?? '—',
      who: d.voidedBy?.fullName ?? d.voidedBy?.username ?? '—',
    };
  }
  if (e.type === 'return') {
    return {
      kind: 'Буцаалт',
      tone: 'bg-warn-bg text-warn-ink',
      action: reasonText ?? 'Буцаагдсан',
      detail: [
        d.disposition ? (RETURN_DISPOSITION_LABEL[d.disposition] ?? d.disposition) : null,
        d.refundMnt ? `буцаалт ${formatMnt(d.refundMnt)}` : null,
        d.notes ?? null,
      ]
        .filter(Boolean)
        .join(' · '),
      who: d.processedBy?.fullName ?? d.processedBy?.username ?? '—',
    };
  }
  if (e.type === 'claim') {
    return {
      kind: 'Баталгаа',
      tone: 'bg-warn-bg text-warn-ink',
      action: `${d.code ?? 'Нэхэмжлэл'} · ${d.defectType?.labelMn ?? 'гэмтэл'}`,
      detail: [
        d.resolution ? (CLAIM_RESOLUTION_LABEL[d.resolution] ?? d.resolution) : null,
        d.rejectReason?.labelMn ?? null,
        d.defectDescription ?? null,
        d.repairCostMnt ? `засвар ${formatMnt(d.repairCostMnt)}` : null,
        d.refundMnt ? `буцаалт ${formatMnt(d.refundMnt)}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      who: d.resolvedBy?.fullName ?? d.resolvedBy?.username ?? '—',
    };
  }
  if (e.type === 'write_off') {
    return {
      kind: 'Актлалт',
      tone: 'bg-alert-bg text-alert-ink',
      action: reasonText ?? 'Актлагдсан',
      detail: [d.lossMnt ? `алдагдал ${formatMnt(d.lossMnt)}` : null, d.note ?? null]
        .filter(Boolean)
        .join(' · '),
      who: d.approvedBy?.fullName ?? d.approvedBy?.username ?? '—',
    };
  }
  if (e.type === 'label') {
    return {
      kind: 'Шошго',
      tone: 'bg-hair/60 text-ink-2',
      action: 'Серийн дугаар олгогдсон',
      detail: d.serial ? String(d.serial) : '—',
      who: d.labeledBy?.username ?? '—',
    };
  }
  return { kind: e.type, tone: 'bg-hair/60 text-ink-2', action: '—', detail: '', who: '—' };
}

export function ItemDetailPage() {
  const { id = '' } = useParams();
  const { can, readOnly } = useAuth();
  const showCost = can(...COST_ROLES);
  const qc = useQueryClient();
  const [action, setAction] = useState<Action>(null);
  const [note, setNote] = useState<string | null>(null);

  const item = useQuery({
    queryKey: ['item', id],
    queryFn: () => api<Envelope<ItemDetail>>(`/items/${id}`),
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['item', id] });
    await qc.invalidateQueries({ queryKey: ['items'] });
    await qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const assignSerial = useMutation({
    mutationFn: () => api<Envelope<{ serial: string }>>(`/items/${id}/assign-serial`, { method: 'POST' }),
    onSuccess: async (res) => {
      setNote(`Серийн дугаар олгогдлоо: ${formatSerialDisplay(res.data.serial)}`);
      await refresh();
    },
    onError: (e: Error) => setNote(e.message),
  });

  // Timeline нь хөдөлгөөн + үнэ + захиалгыг НЭГТГЭСЭН — гутлын
  // бүтэн түүх нэг дор (`/items/:id/movements` зөвхөн зөөлт).
  const timeline = useQuery({
    queryKey: ['item', id, 'timeline'],
    queryFn: () => api<{ data: TimelineEntry[] }>(`/items/${id}/timeline`),
    enabled: !!item.data,
  });

  if (item.isPending) return <Spinner />;
  if (item.error) return <ErrorNote>{(item.error as Error).message}</ErrorNote>;

  const it = item.data.data;

  return (
    <div className="space-y-4">
      <Link to="/items" className="text-sm text-accent-700 hover:underline">← Бараа руу</Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="num text-xl font-semibold tracking-tight">
            {it.serial ? formatSerialDisplay(it.serial) : 'Шошгогүй'}
          </h1>
          <p className="mt-0.5 text-sm text-ink-2">
            {it.model.brand} · {it.model.name} · размер {it.size}
          </p>
        </div>
        <StatusBadge status={it.status} />
      </div>

      {/* ─────────── Үйлдлүүд ─────────── */}
      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-2">
          {!it.serial && can('WAREHOUSE', 'ADMIN') ? (
            <Button onClick={() => assignSerial.mutate()} disabled={assignSerial.isPending}>
              {assignSerial.isPending ? 'Олгож байна…' : 'Серийн дугаар олгох'}
            </Button>
          ) : null}
          {it.serial && can('WAREHOUSE', 'ADMIN') ? (
            <Button variant="ghost" onClick={() => setAction('label')}>Шошго хэвлэх</Button>
          ) : null}
          {can('WAREHOUSE', 'STORE_MANAGER', 'ADMIN') ? (
            <Button variant="ghost" onClick={() => setAction('price')}>Үнэ тогтоох</Button>
          ) : null}
          {can('WAREHOUSE', 'STORE_MANAGER', 'ADMIN') ? (
            <Button variant="ghost" onClick={() => setAction('move')}>Байршил өөрчлөх</Button>
          ) : null}
          {can('STORE_MANAGER', 'ADMIN') && it.status !== 'WRITTEN_OFF' && it.status !== 'SOLD' ? (
            <Button variant="danger" onClick={() => setAction('writeoff')}>Актлах</Button>
          ) : null}
        </div>
      ) : null}

      {note ? (
        <div className="flex items-center gap-3">
          <div className="flex-1"><OkNote>{note}</OkNote></div>
          <button type="button" onClick={() => setNote(null)} className="text-xs text-ink-2 underline">
            Хаах
          </button>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardTitle>Байршил</CardTitle>
          <dl className="space-y-2 text-sm">
            <Row label="Одоогийн байршил" value={formatLocation(it.location)} />
            <Row label="Зарагдах эсэх" value={it.location?.isSellable ? 'Тийм' : 'Үгүй'} />
            <Row label="Шошго наасан" value={it.labeledAt ? formatDate(it.labeledAt) : '—'} />
            <Row label="Sync хийсэн" value={it.syncedAt ? formatDate(it.syncedAt) : 'Хийгээгүй'} />
            <Row label="Хувилбар" value={`v${it.version}`} />
          </dl>
        </Card>

        <Card>
          <CardTitle>Гарал үүсэл</CardTitle>
          <dl className="space-y-2 text-sm">
            <Row label="Нийлүүлэгч" value={`${it.supplier.name}${it.supplier.region ? ` · ${it.supplier.region}` : ''}`} />
            {/* Эхний үлдэгдлийн сав нь аялал биш — кодоо харуулахгүй */}
            <Row
              label="Худалдан авалт"
              value={it.purchase.notes === OPENING_PURCHASE_NOTE ? 'Эхний үлдэгдэл' : (it.purchase.code ?? '—')}
            />
            <Row label="Цуглуулсан" value={formatDate(it.collectedAt)} />
            <Row label="Материал" value={MATERIAL_LABEL[it.model.material] ?? it.model.material} />
            {it.publicToken ? <Row label="QR токен" value={it.publicToken} mono /> : null}
          </dl>
        </Card>

        <Card>
          <CardTitle>Өртөг, үнэ</CardTitle>
          {showCost ? (
            <dl className="space-y-2 text-sm">
              <Row label="Эх үнэ" value={formatRub(it.purchaseAmount)} num />
              <Row
                label="Түгжсэн ханш"
                value={it.purchaseRate ? `1 RUB = ${formatRate(it.purchaseRate)}₮` : 'тооцоогүй'}
                num
              />
              <Row label="Барааны өртөг" value={<Money value={it.goodsCostMnt} />} num />
              <Row label="Нэмэлт зардал" value={<Money value={it.overheadMnt} />} num />
              <Row
                label="Нийт өртөг"
                value={<strong><Money value={it.landedCostMnt} /></strong>}
                num
              />
              <Row label="Жижиглэн үнэ" value={<Money value={it.priceMnt} />} num />
            </dl>
          ) : (
            <dl className="space-y-2 text-sm">
              <Row label="Жижиглэн үнэ" value={<Money value={it.priceMnt} />} num />
              <p className="pt-2 text-xs text-ink-2">Өртгийн мэдээлэл таны эрхэд харагдахгүй.</p>
            </dl>
          )}
        </Card>
      </div>

      {/* Зураг нь баталгааны нотолгоо — 10 жил хадгалагдана */}
      <PhotoPanel base="items" ownerId={id} />

      <Card>
        <CardTitle right={<span className="text-[11.5px] text-ink-3">хөдөлгөөн · үнэ · борлуулалт</span>}>
          Бүтэн түүх
        </CardTitle>
        {timeline.isPending ? (
          <Spinner />
        ) : timeline.error ? (
          <ErrorNote>{(timeline.error as Error).message}</ErrorNote>
        ) : timeline.data.data.length === 0 ? (
          <Empty>Түүх бүртгэгдээгүй</Empty>
        ) : (
          <DataTable
            rows={timeline.data.data}
            columns={timelineColumns()}
            rowKey={(e, i) => `${e.type}-${e.at}-${i}`}
          />
        )}
      </Card>

      {action === 'price' ? (
        <SetPrice
          id={id}
          current={String(it.priceMnt)}
          onClose={() => setAction(null)}
          onDone={async () => { setAction(null); setNote('Үнэ шинэчлэгдлээ'); await refresh(); }}
        />
      ) : null}
      {action === 'move' ? (
        <MoveItem
          id={id}
          hasSerial={!!it.serial}
          onClose={() => setAction(null)}
          onDone={async () => { setAction(null); setNote('Байршил өөрчлөгдлөө'); await refresh(); }}
        />
      ) : null}
      {action === 'writeoff' ? (
        <WriteOffItem
          id={id}
          onClose={() => setAction(null)}
          onDone={async () => { setAction(null); setNote('Гутал актлагдлаа'); await refresh(); }}
        />
      ) : null}
      {action === 'label' ? <PrintLabel id={id} onClose={() => setAction(null)} /> : null}
    </div>
  );
}

// ------------------------------------------------------------ Үнэ
function SetPrice({
  id, current, onClose, onDone,
}: { id: string; current: string; onClose: () => void; onDone: () => void }) {
  const [priceMnt, setPrice] = useState(current === '0' ? '' : current);
  const [reason, setReason] = useState('');

  const run = useMutation({
    mutationFn: () =>
      api(`/items/${id}/price`, {
        method: 'POST',
        body: { priceMnt: Number(priceMnt).toFixed(2), ...(reason.trim() ? { reason: reason.trim() } : {}) },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="Жижиглэн үнэ тогтоох"
      subtitle="Өөрчлөлт бүр үнийн түүхэнд бүртгэгдэнэ"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => run.mutate()} disabled={!Number(priceMnt) || run.isPending}>
            {run.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      <Field label="Үнэ (₮)">
        <Input value={priceMnt} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" autoFocus className="num text-right" />
      </Field>
      <Field label="Шалтгаан">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Жишээ: улирлын хямдрал" />
      </Field>
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ------------------------------------------------------------ Байршил
function MoveItem({
  id, hasSerial, onClose, onDone,
}: { id: string; hasSerial: boolean; onClose: () => void; onDone: () => void }) {
  const [toLocationId, setTo] = useState('');
  const [reason, setReason] = useState('TRANSFER');
  const [note, setNote] = useState('');

  const locations = useQuery({
    queryKey: ['ref-locations'],
    queryFn: () => api<Envelope<Location[]>>('/ref/locations?active=true'),
  });

  const run = useMutation({
    mutationFn: () =>
      api(`/items/${id}/move`, {
        method: 'POST',
        body: { toLocationId, reason, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: onDone,
  });

  const target = (locations.data?.data ?? []).find((l) => l.id === toLocationId);
  const blocked = !!target?.isSellable && !hasSerial;

  return (
    <Modal
      title="Байршил өөрчлөх"
      subtitle="Хөдөлгөөн бүр хөдөлгөөний түүхэнд үлдэнэ"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => run.mutate()} disabled={!toLocationId || blocked || run.isPending}>
            {run.isPending ? 'Зөөж байна…' : 'Зөөх'}
          </Button>
        </>
      }
    >
      {locations.isPending ? <Spinner /> : (
        <>
          <Field label="Очих байршил">
            <Select
              value={toLocationId}
              onChange={setTo}
              placeholder="Сонгох…"
              options={(locations.data?.data ?? []).map((l) => ({
                value: l.id,
                label: `${l.code} · ${l.name}${l.isSellable ? ' (зарагдана)' : ''}`,
              }))}
            />
          </Field>
          <Field label="Шалтгаан">
            <Select
              value={reason}
              onChange={setReason}
              options={[
                { value: 'TRANSFER', label: 'Шилжүүлэг' },
                { value: 'RECEIVE', label: 'Хүлээн авалт' },
                { value: 'LABEL', label: 'Шошгожуулалт' },
                { value: 'REPAIR', label: 'Засвар' },
                { value: 'ADJUST', label: 'Тохируулга' },
              ]}
            />
          </Field>
          <Field label="Тэмдэглэл"><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          {blocked ? (
            <ErrorNote>Шошгогүй гутлыг зарагдах байршилд зөөх боломжгүй — эхлээд серийн дугаар олгоно уу.</ErrorNote>
          ) : null}
          {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
        </>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------ Актлалт
function WriteOffItem({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const [reasonLookupId, setReason] = useState('');
  const [approvedById, setApprover] = useState('');
  const [note, setNote] = useState('');

  const reasons = useQuery({
    queryKey: ['lookups', 'WRITE_OFF_REASON'],
    queryFn: () => api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=WRITE_OFF_REASON&active=true'),
  });
  const users = useQuery({
    queryKey: ['ref-users', 'approvers'],
    queryFn: () => api<Envelope<{ id: string; fullName: string; role: string }[]>>('/ref/users?active=true'),
  });

  const run = useMutation({
    mutationFn: () =>
      api(`/items/${id}/write-off`, {
        method: 'POST',
        body: { reasonLookupId, approvedById, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="Гутал актлах"
      subtitle="Буцаах боломжгүй үйлдэл"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button variant="danger" onClick={() => run.mutate()} disabled={!reasonLookupId || !approvedById || run.isPending}>
            {run.isPending ? 'Актлаж байна…' : 'Актлах'}
          </Button>
        </>
      }
    >
      <Field label="Шалтгаан">
        <Select
          value={reasonLookupId}
          onChange={setReason}
          placeholder="Сонгох…"
          options={(reasons.data?.data ?? []).map((r) => ({ value: r.id, label: r.labelMn }))}
        />
      </Field>
      <Field label="Зөвшөөрсөн хүн">
        <Select
          value={approvedById}
          onChange={setApprover}
          placeholder="Сонгох…"
          options={(users.data?.data ?? [])
            .filter((u) => u.role === 'ADMIN' || u.role === 'STORE_MANAGER')
            .map((u) => ({ value: u.id, label: u.fullName }))}
        />
      </Field>
      <Field label="Тэмдэглэл"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <p className="text-[11.5px] text-ink-3">
        Актлагч ба зөвшөөрөгч өөр хүн байх ёстой.
      </p>
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ------------------------------------------------------------ Шошго
function PrintLabel({ id, onClose }: { id: string; onClose: () => void }) {
  const [widthMm, setWidth] = useState('40');
  const [heightMm, setHeight] = useState('25');
  const [zpl, setZpl] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () =>
      api<Envelope<{ zpl: string }>>(`/items/${id}/print-label`, {
        method: 'POST',
        body: { widthMm: Number(widthMm), heightMm: Number(heightMm) },
      }),
    onSuccess: (res) => setZpl(res.data.zpl),
  });

  return (
    <Modal
      wide
      title="Шошго хэвлэх"
      subtitle="Сервер ZPL үүсгэнэ — принтер рүү TCP :9100-аар илгээнэ"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
          {zpl ? (
            <Button onClick={() => void navigator.clipboard.writeText(zpl)}>ZPL хуулах</Button>
          ) : (
            <Button onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Үүсгэж байна…' : 'ZPL үүсгэх'}
            </Button>
          )}
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Өргөн (мм)"><Input value={widthMm} onChange={(e) => setWidth(e.target.value)} inputMode="numeric" className="num" /></Field>
        <Field label="Өндөр (мм)"><Input value={heightMm} onChange={(e) => setHeight(e.target.value)} inputMode="numeric" className="num" /></Field>
      </div>
      {zpl ? (
        <>
          <p className="text-[12.5px] text-ink-2">
            Хуулаад принтер рүү илгээнэ үү. Веб хөтчөөс TCP холболт хийх боломжгүй.
          </p>
          <pre className="max-h-64 overflow-auto rounded-card border border-hair bg-well p-3 font-mono text-[11px] whitespace-pre-wrap">
            {zpl}
          </pre>
        </>
      ) : null}
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

function Row({
  label, value, num = false, mono = false,
}: {
  label: string;
  value: React.ReactNode;
  num?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-ink-2">{label}</dt>
      <dd className={`text-right ${num ? 'num' : ''} ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

/** Гутлын бүтэн түүх — 640px-ээс доош үйл явдал тус бүрийн карт. */
function timelineColumns(): Column<TimelineEntry>[] {
  return [
    {
      key: 'at',
      header: 'Огноо',
      align: 'right',
      cell: (e) => <span className="text-ink-2">{formatDateTime(e.at)}</span>,
    },
    {
      key: 'kind',
      header: 'Төрөл',
      cell: (e) => {
        const v = describe(e);
        return (
          <span className={`inline-block rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${v.tone}`}>
            {v.kind}
          </span>
        );
      },
    },
    {
      key: 'action',
      header: 'Үйлдэл',
      mobile: 'title',
      cell: (e) => <span className="font-medium text-pretty">{describe(e).action}</span>,
    },
    // Кирилл тайлбар урт — `nowrap` тавихгүй, боогдоно
    { key: 'detail', header: 'Дэлгэрэнгүй', cell: (e) => <span className="text-ink-2 text-pretty">{describe(e).detail}</span> },
    { key: 'who', header: 'Хэн', cell: (e) => <span className="text-ink-2 text-pretty">{describe(e).who}</span> },
  ];
}
